import React from 'react';

import { AssociatedPurchaseOrdersField } from 'components/logistics/components/crossDockPurchaseOrders/create/AssociatedPurchaseOrdersField';
import { CrossDockPurchaseOrderServiceFeeSection } from 'components/logistics/components/crossDockPurchaseOrders/create/CrossDockPurchaseOrderServiceFeeSection';
import {
    formatCrossDockPurchaseOrderCreatedOn,
    formatCrossDockPurchaseOrderScheduledDelivery,
} from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderUtils';
import { formatPurchaseOrderDate } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import {
    FALLBACK_TEXT,
    formatNumber,
    normalizeOptionalText,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';

const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';
const valueClassName = 'text-left text-sm font-medium text-foreground';

/**
 * @param {Object} props
 * @param {Object} [props.order]
 * @param {string|number} [props.facilityName]
 * @param {string|number} [props.crossDockName]
 * @param {Array<{ chargeType: string, label: string, required: boolean }>} [props.additionalFeeTypes]
 */
export function CrossDockPurchaseOrderPrimaryInfoCard({ additionalFeeTypes = [], crossDockName, facilityName, order }) {
    const fields = [
        { label: 'Destination Cross Dock', value: normalizeOptionalText(crossDockName) },
        { label: 'Facility', value: normalizeOptionalText(facilityName) },
        { label: 'Dock Door', value: normalizeOptionalText(order?.dockDoor) },
        { label: 'Scheduled Delivery', value: formatCrossDockPurchaseOrderScheduledDelivery(order) },
        { label: 'Ship Date', value: formatPurchaseOrderDate(order?.shipDate, FALLBACK_TEXT) },
        {
            label: 'Min Temperature',
            value: order?.minTemp != null ? formatNumber(order.minTemp) : FALLBACK_TEXT,
        },
        {
            label: 'Max Temperature',
            value: order?.maxTemp != null ? formatNumber(order.maxTemp) : FALLBACK_TEXT,
        },
        { label: 'PO Type', value: normalizeOptionalText(order?.poType) },
        { label: 'Created On', value: formatCrossDockPurchaseOrderCreatedOn(order?.createdAt) },
    ];

    return (
        <Card>
            <CardHeader className="pb-0">
                <CardTitle>Cross Dock Purchase Order</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 xl:grid-cols-12">
                    {fields.map(({ fullWidth, label, value }) => (
                        <div
                            key={label}
                            className={
                                fullWidth ? 'flex flex-col gap-2 xl:col-span-12' : 'flex flex-col gap-2 xl:col-span-3'
                            }
                        >
                            <span className={labelClassName}>{label}</span>
                            <span className={valueClassName}>{value}</span>
                        </div>
                    ))}

                    <div className="xl:col-span-12">
                        <CrossDockPurchaseOrderServiceFeeSection
                            draft={{
                                crossDockId: order?.crossDockId,
                                additionalFees: order?.additionalFees ?? [],
                            }}
                            additionalFeeTypes={additionalFeeTypes}
                            readOnly
                        />
                    </div>

                    <AssociatedPurchaseOrdersField poMaps={order?.poMaps ?? []} readOnly />

                    <div className="flex flex-col gap-2 xl:col-span-12">
                        <span className={labelClassName}>Special Instructions</span>
                        <span className={valueClassName}>{normalizeOptionalText(order?.specialInstructions)}</span>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
